import { type FC, useEffect, useMemo } from 'react'
import { useForm, Controller } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useGetEnrollmentDisclosuresBrokerageData, useSubmitEnrollmentDisclosuresBrokerageData } from '@egw/api'
import { Button, Alert, AlertType, GenericError } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentLayout } from '@egw/layouts'
import { useRouter } from '@egw/router'
import { configClient } from '@egw/config'
import { WEALTH_ROUTES } from '@egw/constants'
import { ROUTES } from '../../../constants'
import { DisclosureSection, PageHeader } from '../../../components'
import { type Config } from '../../../config/config'
import { AccountTypeEnum } from '../../../constants/api/account-type-enum'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { geBrokerageCompanionInvestmentDisclosures } from '../../../utils/disclosures/helpers/get-brokerage-companion-investment-disclosures'
import {
  getPayloadBrokerage,
  getPayloadIRA,
} from '../../../utils/disclosures/helpers/get-brokerage-disclosures-payload'

const log = new Logger('Enrollment - Disclosures Companion')

type DisclosuresOnPage =
  | 'dw_account_agreements'
  | 'dw_portfolio_management_agreements'
  | 'dw_rule14b_1c'
  | 'eg_wealth_advisors'

interface Props {
  accountType: 'brokerage' | 'traditional'
}

export const DisclosuresCompanionPage: FC<Props> = ({ accountType }) => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()

  const config = configClient.getConfig() as Config

  const { data, isLoading, error } = useGetEnrollmentDisclosuresBrokerageData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentDisclosuresBrokerageData()

  const includeCompanionAccount = data?.enrollmentData?.investmentProfile.account.includeCompanionAccount

  // If Companion Account should not be created, redirect to dashboard
  useEffect(() => {
    if (includeCompanionAccount === false) {
      navigate(WEALTH_ROUTES.HOME.path)
    }
  }, [includeCompanionAccount])

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({})

  const disclosuresData = useMemo(() => {
    return geBrokerageCompanionInvestmentDisclosures(config.env)
  }, [config.env])

  const disclosureSections: DisclosuresOnPage[] = useMemo(() => {
    const _disclosureSections: DisclosuresOnPage[] = []
    for (const key in disclosuresData) {
      _disclosureSections.push(key as DisclosuresOnPage)
    }
    return _disclosureSections
  }, [disclosuresData])

  // Route validation
  // useValidateRoute(data?.path) // Disabled

  const onSubmit = handleSubmit(async (): Promise<void> => {
    try {
      const _payload =
        accountType === AccountTypeEnum.BROKERAGE ? getPayloadBrokerage(config.env) : getPayloadIRA(config.env)
      const response = await mutate(_payload)
      if (response?.path) {
        navigate(response.path)
      } else {
        // TODO: Handle the case when path is not returned
        navigate(ROUTES.HOME.path)
      }
    } catch (e) {
      log.error('Error submitting email', e)
    }
  })

  return (
    <EnrollmentLayout title="Disclosures">
      <div className="egw-container-enroll h-full flex flex-col">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error && !submitError ? (
          <>
            <PageHeader title={t('investments.disclosures_companion.title')} />
            <p className="mb-sm typo-primary-sm-regular text-text-meta">
              {t('investments.disclosures_companion.description')}
            </p>
            <form onSubmit={(e) => void onSubmit(e)} className="flex-1 flex flex-col justify-start md:justify-start">
              {disclosureSections.map((e, i) => {
                return (
                  <Controller
                    name={e}
                    control={control}
                    rules={{
                      required: t('common.required_field'),
                    }}
                    render={({ field }) => (
                      <DisclosureSection
                        checkboxHelperText={disclosuresData[e].helper}
                        checkboxLabel={disclosuresData[e].title}
                        expandedLinks={disclosuresData[e].links}
                        expandedText={disclosuresData[e].expanded}
                        hasBorder={disclosureSections.length !== i + 1}
                        testId={`disclosure-${disclosuresData[e].title.replaceAll(' ', '-')}`}
                        onCheckboxChange={field.onChange}
                      />
                    )}
                    key={e}
                  />
                )
              })}

              <div className="flex-1 md:hidden" />

              {submitError ? <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" /> : null}

              {/* AUTO GENERATED COMPONENTS */}

              <Button
                type="submit"
                className="w-full md:w-min mt-lg"
                isDisabled={!isValid}
                isLoading={isSubmitting}
                testId="enrollment-continue-button"
              >
                {t('common.continue')}
              </Button>
            </form>
          </>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentLayout>
  )
}
